import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryApi, FilterCondition, FilterOrGroup, QueryRequest, SavedQuery } from '../api/queryApi';
import { documentsApi } from '../api/adminApi';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Plus, Trash2, Play, X, Code, ExternalLink, RefreshCw, Save, BookOpen, ChevronDown } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import DocumentEditor from '../components/DocumentEditor';
import { QueryResults } from '../components/query-builder/QueryResults';
import type { DocumentResult } from '../components/query-builder/types';

interface FilterGroup {
  id: string;
  filters: FilterCondition[];
  isOrGroup: boolean;
}

const OPERATORS: FilterCondition['operator'][] = [
  '==',
  '!=',
  '<',
  '<=',
  '>',
  '>=',
  'in',
  'array-contains',
];

const QueryBuilder: React.FC = () => {
  const { isAuthenticated } = useAuthStore();
  const queryClient = useQueryClient();

  // Form/Query Configuration State
  const [collection, setCollection] = useState('');
  const [collections, setCollections] = useState<string[]>(['']);
  const [multiCollectionMode, setMultiCollectionMode] = useState<boolean>(false);
  const [filterGroups, setFilterGroups] = useState<FilterGroup[]>([]);
  const [orderBy, setOrderBy] = useState<Array<{ field: string; direction: 'asc' | 'desc' }>>([]);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [collectionFilter, setCollectionFilter] = useState<string>('');
  const [cursors, setCursors] = useState<string[]>([]); // Track cursors for each page

  // Control when query executes (for manual execute button)
  const [shouldExecuteQuery, setShouldExecuteQuery] = useState(false);
  const [queryKeySnapshot, setQueryKeySnapshot] = useState<any[]>([]);

  // UI State
  const [showJson, setShowJson] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  // Raw-edit modal target (super_admin only)
  const [editTarget, setEditTarget] = useState<{ collection: string; id: string } | null>(null);
  const [saveQueryName, setSaveQueryName] = useState('');
  const [saveQueryDescription, setSaveQueryDescription] = useState('');
  const [showLoadDropdown, setShowLoadDropdown] = useState(false);
  const [showCollectionDropdown, setShowCollectionDropdown] = useState(false);
  const [multiCollectionDropdowns, setMultiCollectionDropdowns] = useState<Record<number, boolean>>({});
  
  // Fetch saved queries - only when authenticated (no pagination needed)
  const { data: savedQueriesResponse, refetch: refetchSavedQueries, isLoading: isLoadingQueries, error: queriesError } = useQuery({
    queryKey: ['savedQueries'],
    queryFn: () => queryApi.getSavedQueries(1, 100), // Fetch first 100 queries
    enabled: isAuthenticated && showLoadDropdown,
    retry: false,
  });
  
  const savedQueries = savedQueriesResponse?.data || [];

  // Fetch available collections
  const { 
    data: availableCollections = [], 
    isLoading: collectionsLoading,
    error: collectionsError 
  } = useQuery({
    queryKey: ['collections'],
    queryFn: async () => {
      try {
        const collections = await queryApi.getCollections();
        return collections;
      } catch (error) {
        console.error('Failed to fetch collections:', error);
        // Return default collections as fallback
        return ['syncedItems', 'configs', 'data', 'polling'];
      }
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // No auto-initialization - let user select from dropdown

  // Click outside handler for collection dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.collection-dropdown-container')) {
        setShowCollectionDropdown(false);
        setMultiCollectionDropdowns({});
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDocumentClick = (documentId: string, docCollection?: string) => {
    const targetCollection = docCollection || collection;
    if (targetCollection && documentId) {
      setEditTarget({ collection: targetCollection, id: documentId });
    }
  };

  // The modal always edits a freshly fetched document — query results may be
  // field-projected (select) or stale, and a raw save writes whole fields.
  const {
    data: editDocument,
    isLoading: editDocumentLoading,
    error: editDocumentError,
  } = useQuery({
    queryKey: ['raw-document', editTarget?.collection, editTarget?.id],
    queryFn: () => documentsApi.get(editTarget!.collection, editTarget!.id),
    enabled: !!editTarget,
    staleTime: 0,
    gcTime: 0,
  });

  const handleRawDocumentSave = async (data: Record<string, unknown>) => {
    if (!editTarget) return;
    try {
      // _updateTime from the fresh GET rides along so the server rejects the
      // save if the document changed while the modal was open.
      await documentsApi.update(editTarget.collection, editTarget.id, {
        ...data,
        ...(editDocument?._updateTime !== undefined
          ? { _updateTime: editDocument._updateTime }
          : {}),
      });
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 409) {
        throw new Error(
          'This document changed while the editor was open. Close the editor and reopen it to load the latest version.',
        );
      }
      throw error;
    }
    queryClient.invalidateQueries({ queryKey: ['firestore-query'] });
    setEditTarget(null);
  };

  // Main query execution using useQuery
  const { 
    data: queryData, 
    error: queryError,
    isLoading: queryIsLoading,
    refetch: refetchQuery 
  } = useQuery({
    queryKey: ['firestore-query', ...queryKeySnapshot],
    queryFn: async () => {
      const buildQueryRequest = (collectionName?: string): QueryRequest => {
        const filters: FilterOrGroup[] = filterGroups.map((group) => {
          const validFilters = group.filters
            .filter((f) => f.field && f.value !== '')
            .map((f) => ({
              ...f,
              value: parseValue(String(f.value), f.operator),
            }));
          if (group.isOrGroup && validFilters.length >= 2) {
            return validFilters;
          } else if (!group.isOrGroup && validFilters.length > 0) {
            return validFilters[0];
          }
          return null;
        }).filter((f): f is FilterOrGroup => f !== null);

        const validOrderBy = orderBy.filter((o) => o.field);
        const validSelect = selectedFields.filter((f) => f.trim() !== '');

        const result: QueryRequest = {
          collection: collectionName || collection,
          filters,
          limit: pageSize,
        };
        
        if (validOrderBy.length > 0) {
          result.orderBy = validOrderBy;
        }
        
        if (validSelect.length > 0) {
          result.select = validSelect;
        }
        
        // Add cursor for pagination (only on subsequent pages)
        if (currentPage > 1 && cursors[currentPage - 2]) {
          result.cursor = cursors[currentPage - 2];
        }
        
        return result;
      };

      const buildQueries = (): QueryRequest[] => {
        if (!multiCollectionMode) {
          return [buildQueryRequest(undefined)];
        }
        
        if (collectionFilter) {
          return [buildQueryRequest(collectionFilter)];
        }
        
        return collections
          .filter(col => col.trim() !== '')
          .map(col => buildQueryRequest(col));
      };
      
      const queries = buildQueries();
      const data = await queryApi.execute(queries);
      
      if (!data || data.length === 0) return null;
      
      // Process results
      if (multiCollectionMode && !collectionFilter && data.length > 1) {
        const mergedResults: DocumentResult[] = [];
        let totalCount = 0;
        let hasMore = false;
        const queryTimes: number[] = [];
        
        data.forEach((result, index) => {
          if (result.success) {
            const collectionName = collections[index] || `collection_${index}`;
            result.data.forEach((doc: Record<string, unknown>) => {
              mergedResults.push({
                ...doc,
                _collection: collectionName,
              });
            });
            totalCount += result.metadata.count;
            hasMore = hasMore || result.metadata.hasMore;
            const timeMatch = result.metadata.queryTime.match(/\d+/);
            if (timeMatch) {
              queryTimes.push(parseInt(timeMatch[0]));
            }
          }
        });
        
        return {
          results: mergedResults,
          metadata: {
            count: totalCount,
            hasMore,
            queryTime: `${Math.max(...queryTimes)}ms (max)`,
          }
        };
      } else {
        if (data[0]?.success) {
          const queriedCollection = collectionFilter || (multiCollectionMode ? collections[0] : collection);
          const resultsWithCollection = (data[0].data || []).map((doc: Record<string, unknown>) => ({
            ...doc,
            _collection: queriedCollection,
          }));
          
          return {
            results: resultsWithCollection,
            metadata: data[0].metadata || null
          };
        } else {
          throw new Error(data[0]?.error || 'Query failed');
        }
      }
    },
    enabled: isAuthenticated && shouldExecuteQuery && queryKeySnapshot.length > 0,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
  });
  
  // Update cursors when query data changes
  useEffect(() => {
    if (queryData?.metadata?.cursor && queryData.metadata.hasMore) {
      setCursors(prev => {
        const newCursors = [...prev];
        newCursors[currentPage - 1] = queryData.metadata.cursor;
        return newCursors;
      });
    }
  }, [queryData, currentPage]);

  const addFilterGroup = (isOrGroup = false) => {
    const newGroup: FilterGroup = {
      id: Date.now().toString(),
      filters: [{ field: '', operator: '==', value: '' }],
      isOrGroup,
    };
    setFilterGroups([...filterGroups, newGroup]);
  };

  const removeFilterGroup = (groupId: string) => {
    setFilterGroups(filterGroups.filter((g) => g.id !== groupId));
  };

  const addFilterToGroup = (groupId: string) => {
    setFilterGroups(
      filterGroups.map((g) =>
        g.id === groupId
          ? { ...g, filters: [...g.filters, { field: '', operator: '==', value: '' }] }
          : g
      )
    );
  };

  const removeFilterFromGroup = (groupId: string, filterIndex: number) => {
    setFilterGroups(
      filterGroups.map((g) =>
        g.id === groupId
          ? { ...g, filters: g.filters.filter((_, i) => i !== filterIndex) }
          : g
      )
    );
  };

  const parseValue = (value: string, operator: FilterCondition['operator']): string | number | boolean | string[] => {
    if (operator === 'in') {
      // For 'in' operator, expect comma-separated values
      return value.split(',').map((v) => v.trim()).filter((v) => v !== '');
    }
    // Try to parse as number if it looks like one
    if (/^-?\d+(\.\d+)?$/.test(value.trim())) {
      return Number(value);
    }
    // Try to parse as boolean
    if (value.trim().toLowerCase() === 'true') return true;
    if (value.trim().toLowerCase() === 'false') return false;
    // Return as string
    return value;
  };

  const updateFilter = (
    groupId: string,
    filterIndex: number,
    field: 'field' | 'operator' | 'value',
    value: string | FilterCondition['operator']
  ) => {
    setFilterGroups(
      filterGroups.map((g) =>
        g.id === groupId
          ? {
              ...g,
              filters: g.filters.map((f, i) => {
                if (i === filterIndex) {
                  const updated = { ...f, [field]: value };
                  // If operator changed and it's 'in', we might need to handle value differently
                  if (field === 'operator' && value === 'in' && typeof f.value === 'string') {
                    // Convert current value to array format for 'in' operator
                    updated.value = f.value.split(',').map((v: string) => v.trim()).filter((v: string) => v !== '').join(', ');
                  }
                  return updated;
                }
                return f;
              }),
            }
          : g
      )
    );
  };

  const addOrderBy = () => {
    setOrderBy([...orderBy, { field: '', direction: 'asc' }]);
  };

  const removeOrderBy = (index: number) => {
    setOrderBy(orderBy.filter((_, i) => i !== index));
  };

  const updateOrderBy = (index: number, field: 'field' | 'direction', value: string | 'asc' | 'desc') => {
    setOrderBy(orderBy.map((o, i) => (i === index ? { ...o, [field]: value } : o)));
  };

  const addSelectedField = () => {
    setSelectedFields([...selectedFields, '']);
  };

  const removeSelectedField = (index: number) => {
    setSelectedFields(selectedFields.filter((_, i) => i !== index));
  };

  const updateSelectedField = (index: number, value: string) => {
    setSelectedFields(selectedFields.map((f, i) => (i === index ? value : f)));
  };

  const buildQuery = (collectionName?: string): QueryRequest => {
    const filters: FilterOrGroup[] = filterGroups.map((group) => {
      const validFilters = group.filters
        .filter((f) => f.field && f.value !== '')
        .map((f) => ({
          ...f,
          value: parseValue(String(f.value), f.operator),
        }));
      if (group.isOrGroup && validFilters.length >= 2) {
        return validFilters;
      } else if (!group.isOrGroup && validFilters.length > 0) {
        return validFilters[0];
      }
      return null;
    }).filter((f): f is FilterOrGroup => f !== null);

    const validOrderBy = orderBy.filter((o) => o.field);
    const validSelect = selectedFields.filter((f) => f.trim() !== '');

    // Calculate cursor based on current page and previous query data
    let cursor: string | undefined;
    if (currentPage > 1 && queryData?.metadata?.cursor) {
      cursor = queryData.metadata.cursor;
    }

    const result: QueryRequest = {
      collection: collectionName || collection,
      filters,
      limit: pageSize,
    };
    
    if (validOrderBy.length > 0) {
      result.orderBy = validOrderBy;
    }
    
    if (validSelect.length > 0) {
      result.select = validSelect;
    }
    
    if (cursor) {
      result.cursor = cursor;
    }
    
    return result;
  };
  
  const buildMultiCollectionQueries = (): QueryRequest[] => {
    // If not in multi-collection mode, build single query
    if (!multiCollectionMode) {
      return [buildQuery(undefined)];
    }
    
    // If collection filter is set, query ONLY that collection (enables pagination)
    if (collectionFilter) {
      return [buildQuery(collectionFilter)];
    }
    
    // Otherwise query all collections (no cursor - just merge results)
    return collections
      .filter(col => col.trim() !== '')
      .map(col => buildQuery(col));
  };

  // Execute query button handler
  const handleExecute = () => {
    // Snapshot current query configuration
    const newQueryKey = [
      multiCollectionMode ? collections : collection,
      multiCollectionMode,
      collectionFilter,
      JSON.stringify(filterGroups), // Stringify for stable comparison
      JSON.stringify(orderBy),
      JSON.stringify(selectedFields),
      pageSize,
      1, // Always reset to page 1 on new query
    ];
    setQueryKeySnapshot(newQueryKey);
    setCurrentPage(1);
    setCursors([]); // Clear cursors when starting new query
    setShouldExecuteQuery(true);
  };
  
  // Navigate to next page
  const handleNextPage = () => {
    if (queryData?.metadata?.hasMore) {
      const newPage = currentPage + 1;
      const newQueryKey = [
        multiCollectionMode ? collections : collection,
        multiCollectionMode,
        collectionFilter,
        JSON.stringify(filterGroups),
        JSON.stringify(orderBy),
        JSON.stringify(selectedFields),
        pageSize,
        newPage,
      ];
      setQueryKeySnapshot(newQueryKey);
      setCurrentPage(newPage);
    }
  };
  
  // Navigate to previous page
  const handlePrevPage = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      const newQueryKey = [
        multiCollectionMode ? collections : collection,
        multiCollectionMode,
        collectionFilter,
        JSON.stringify(filterGroups),
        JSON.stringify(orderBy),
        JSON.stringify(selectedFields),
        pageSize,
        newPage,
      ];
      setQueryKeySnapshot(newQueryKey);
      setCurrentPage(newPage);
    }
  };

  // Handler for collection filter changes
  const handleCollectionFilterChange = (newFilter: string) => {
    setCollectionFilter(newFilter);
    setCurrentPage(1);
    // Don't auto-execute, let user click Execute
  };

  // Handler for page size changes
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
    setCursors([]); // Clear cursors when changing page size
    
    // Auto-execute with new page size
    const newQueryKey = [
      multiCollectionMode ? collections : collection,
      multiCollectionMode,
      collectionFilter,
      JSON.stringify(filterGroups),
      JSON.stringify(orderBy),
      JSON.stringify(selectedFields),
      newSize, // Use new page size
      1, // Reset to page 1
    ];
    setQueryKeySnapshot(newQueryKey);
  };

  const getQueryJson = () => {
    return JSON.stringify(buildMultiCollectionQueries(), null, 2);
  };
  
  // Multi-collection management
  const addCollection = () => {
    setCollections([...collections, '']);
  };
  
  const removeCollection = (index: number) => {
    if (collections.length > 1) {
      setCollections(collections.filter((_, i) => i !== index));
    }
  };
  
  const updateCollection = (index: number, value: string) => {
    setCollections(collections.map((c, i) => (i === index ? value : c)));
  };

  // Load a saved query into the builder
  const loadSavedQuery = (savedQuery: SavedQuery) => {
    if (savedQuery.query && savedQuery.query.length > 0) {
      const query = savedQuery.query[0];
      setCollection(query.collection || 'syncedItems');
      
      // Reconstruct filter groups from query filters
      const newFilterGroups: FilterGroup[] = [];
      query.filters?.forEach((filter, index) => {
        if (Array.isArray(filter)) {
          // OR group
          newFilterGroups.push({
            id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${index}-${Math.random()}`,
            filters: filter as FilterCondition[],
            isOrGroup: true,
          });
        } else {
          // AND filter
          newFilterGroups.push({
            id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${index}-${Math.random()}`,
            filters: [filter as FilterCondition],
            isOrGroup: false,
          });
        }
      });
      setFilterGroups(newFilterGroups);
      
      setOrderBy(query.orderBy || []);
      setSelectedFields(query.select || ['']);
      setPageSize(query.limit || 20);
      setShowLoadDropdown(false);
    }
  };

  // Save current query
  const saveQueryMutation = useMutation({
    mutationFn: (data: { name: string; description?: string }) => {
      const payload: { name: string; description?: string; query: QueryRequest[] } = {
        name: data.name,
        query: [buildQuery()],
      };
      if (data.description) {
        payload.description = data.description;
      }
      return queryApi.saveQuery(payload);
    },
    onSuccess: () => {
      setShowSaveModal(false);
      setSaveQueryName('');
      setSaveQueryDescription('');
      refetchSavedQueries();
    },
  });

  const handleSaveQuery = () => {
    if (!saveQueryName.trim()) {
      alert('Query name is required');
      return;
    }
    const payload: { name: string; description?: string } = {
      name: saveQueryName.trim(),
    };
    const trimmedDescription = saveQueryDescription.trim();
    if (trimmedDescription) {
      payload.description = trimmedDescription;
    }
    saveQueryMutation.mutate(payload);
  };

  return (
    <div className="page-container" style={{ maxWidth: '1400px', margin: '0 auto', padding: '2rem' }}>
      <div className="hero-gradient" style={{ marginBottom: '2rem', paddingBottom: '1.5rem' }}>
        <h1 className="trustflight-heading" style={{ fontSize: '2.5rem', fontWeight: 700, marginBottom: '0.75rem', color: 'var(--primary-color)', lineHeight: '1.2' }}>
          Query Builder
        </h1>
        <p style={{ fontSize: '1.125rem', lineHeight: '1.7', color: 'var(--text-dark)', fontWeight: 400, maxWidth: '800px' }}>
          Build and execute Firestore queries with filters, sorting, and field selection
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>
        <Card style={{ backgroundColor: 'var(--bg-darker)', border: '1px solid var(--border-color)' }}>
          <CardHeader style={{ borderBottom: '1px solid var(--border-color)', padding: '1.5rem 1.5rem 1rem 1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <div>
                <CardTitle style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-dark)', marginBottom: '0.25rem' }}>
                  Query Configuration
                </CardTitle>
                <CardDescription style={{ fontSize: '0.875rem', color: 'var(--text-medium)', fontWeight: 500 }}>
                  Configure your query parameters
                </CardDescription>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    onClick={() => {
                      const newShowLoadDropdown = !showLoadDropdown;
                      setShowLoadDropdown(newShowLoadDropdown);
                      if (newShowLoadDropdown) {
                        refetchSavedQueries();
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.375rem',
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      padding: '0.375rem 0.75rem',
                      backgroundColor: 'var(--primary-color)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.375rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = '0.9';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '1';
                    }}
                  >
                    <BookOpen size={13} />
                    Load
                    <ChevronDown size={13} />
                  </button>
                  {showLoadDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: '0.25rem',
                      backgroundColor: 'var(--bg-light)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '0.375rem',
                      boxShadow: 'var(--shadow-md)',
                      zIndex: 1000,
                      minWidth: '300px',
                      maxHeight: '400px',
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden'
                    }}>
                      {(() => {
                        if (isLoadingQueries) {
                          return (
                            <div style={{ padding: '1rem', textAlign: 'center', fontSize: '0.875rem', color: 'var(--text-medium)' }}>
                              Loading queries...
                            </div>
                          );
                        }
                        
                        if (queriesError) {
                          return (
                            <div style={{ padding: '1rem', textAlign: 'center', fontSize: '0.875rem', color: 'var(--danger)' }}>
                              Error loading queries: {queriesError instanceof Error ? queriesError.message : 'Unknown error'}
                            </div>
                          );
                        }
                        
                        if (!savedQueries || savedQueries.length === 0) {
                          return (
                            <div style={{ padding: '1rem', textAlign: 'center', fontSize: '0.875rem', color: 'var(--text-medium)' }}>
                              No saved queries
                            </div>
                          );
                        }
                        
                        return (
                          <>
                            <div style={{ flex: 1, overflowY: 'auto' }}>
                            {savedQueries.map((sq, index) => (
                              <div
                                key={sq.id || `query-${index}`}
                                onClick={() => loadSavedQuery(sq)}
                                style={{
                                  padding: '0.75rem 1rem',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid var(--border-color)',
                                  transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = 'var(--bg-darker)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                              >
                                <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-dark)', marginBottom: '0.25rem' }}>
                                  {sq.name}
                                </div>
                                {sq.description && (
                                  <div style={{ fontSize: '0.75rem', color: 'var(--text-medium)' }}>
                                    {sq.description}
                                  </div>
                                )}
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-medium)', marginTop: '0.25rem' }}>
                                  Used {sq.usageCount || 0} times
                                </div>
                              </div>
                            ))}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setShowSaveModal(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    padding: '0.375rem 0.75rem',
                    backgroundColor: 'var(--primary-color)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '0.9';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '1';
                  }}
                >
                  <Save size={13} />
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (confirm('Clear all query fields? This cannot be undone.')) {
                      setCollection('');
                      setCollections(['']);
                      setMultiCollectionMode(false);
                      setFilterGroups([]);
                      setOrderBy([]);
                      setSelectedFields([]);
                      setCurrentPage(1);
                      setCollectionFilter('');
                      setCursors([]);
                      setShouldExecuteQuery(false);
                      setQueryKeySnapshot([]);
                      // Clear any cached query results
                      queryClient.removeQueries({ queryKey: ['firestore-query'] });
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    padding: '0.375rem 0.75rem',
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#b91c1c';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#dc2626';
                  }}
                >
                  <Trash2 size={13} />
                  Clear
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <label htmlFor="collection" className="form-label" style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-dark)' }}>
                  Collection{multiCollectionMode ? 's' : ''}
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem', color: 'var(--text-medium)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={multiCollectionMode}
                    onChange={(e) => {
                      setMultiCollectionMode(e.target.checked);
                      if (e.target.checked) {
                        // Sync collections with current collection when enabling (keep empty if empty)
                        setCollections([collection]);
                      } else {
                        // Sync collection with first collections item when disabling (keep empty if empty)
                        setCollection(collections[0] || '');
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                  Multi-collection mode
                </label>
              </div>
              
              {!multiCollectionMode ? (
                <div className="collection-dropdown-container" style={{ position: 'relative' }}>
                <input
                  type="text"
                    id="collection"
                  className="input-field"
                  value={collection}
                  onChange={(e) => setCollection(e.target.value)}
                    onFocus={() => setShowCollectionDropdown(true)}
                    placeholder="Type to filter or enter custom path..."
                    style={{ fontSize: '0.875rem', width: '100%', paddingRight: collection ? '4.5rem' : '2.5rem' }}
                    disabled={collectionsLoading}
                    autoComplete="off"
                  />
                  {collection && (
                    <button
                      type="button"
                      onClick={() => setCollection('')}
                      style={{
                        position: 'absolute',
                        right: '2rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        padding: '0.25rem',
                        display: 'flex',
                        alignItems: 'center',
                        color: 'var(--text-medium)',
                        transition: 'color 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#dc2626';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'var(--text-medium)';
                      }}
                    >
                      <X size={16} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowCollectionDropdown(!showCollectionDropdown)}
                    disabled={collectionsLoading}
                    style={{
                      position: 'absolute',
                      right: '0.5rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'transparent',
                      cursor: collectionsLoading ? 'not-allowed' : 'pointer',
                      padding: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      color: 'var(--text-medium)',
                      transition: 'color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!collectionsLoading) {
                        e.currentTarget.style.color = 'var(--primary-color)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = 'var(--text-medium)';
                    }}
                  >
                    <ChevronDown size={16} />
                  </button>
                  {showCollectionDropdown && availableCollections.length > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      backgroundColor: 'var(--bg-light)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '0.375rem',
                      marginTop: '0.25rem',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                      zIndex: 1000
                    }}>
                      {availableCollections
                        .filter(col => col.toLowerCase().includes(collection.toLowerCase()))
                        .map((col) => (
                          <div
                            key={col}
                            onClick={() => {
                              setCollection(col);
                              setShowCollectionDropdown(false);
                            }}
                            style={{
                              padding: '0.5rem 0.75rem',
                              cursor: 'pointer',
                              fontSize: '0.875rem',
                              color: 'var(--text-dark)',
                              backgroundColor: collection === col ? 'var(--primary-color)' : 'transparent',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              if (collection !== col) {
                                e.currentTarget.style.backgroundColor = 'var(--bg-darker)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (collection !== col) {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            {col}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', width: '100%' }}>
                  {collections.map((col, index) => (
                    <div key={index} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', width: '100%' }}>
                      <div className="collection-dropdown-container" style={{ position: 'relative', flex: '1 1 0', minWidth: 0 }}>
                      <input
                        type="text"
                        className="input-field"
                        value={col}
                        onChange={(e) => updateCollection(index, e.target.value)}
                          onFocus={() => setMultiCollectionDropdowns(prev => ({ ...prev, [index]: true }))}
                          placeholder="Type to filter or enter custom path..."
                          style={{ fontSize: '0.875rem', width: '100%', paddingRight: col ? '4.5rem' : '2.5rem' }}
                          disabled={collectionsLoading}
                          autoComplete="off"
                        />
                        {col && (
                          <button
                            type="button"
                            onClick={() => updateCollection(index, '')}
                            style={{
                              position: 'absolute',
                              right: '2rem',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              padding: '0.25rem',
                              display: 'flex',
                              alignItems: 'center',
                              color: 'var(--text-medium)',
                              transition: 'color 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = '#dc2626';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = 'var(--text-medium)';
                            }}
                          >
                            <X size={16} />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setMultiCollectionDropdowns(prev => ({ ...prev, [index]: !prev[index] }))}
                          disabled={collectionsLoading}
                          style={{
                            position: 'absolute',
                            right: '0.5rem',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            border: 'none',
                            background: 'transparent',
                            cursor: collectionsLoading ? 'not-allowed' : 'pointer',
                            padding: '0.25rem',
                            display: 'flex',
                            alignItems: 'center',
                            color: 'var(--text-medium)',
                            transition: 'color 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            if (!collectionsLoading) {
                              e.currentTarget.style.color = 'var(--primary-color)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = 'var(--text-medium)';
                          }}
                        >
                          <ChevronDown size={16} />
                        </button>
                        {multiCollectionDropdowns[index] && availableCollections.length > 0 && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            maxHeight: '200px',
                            overflowY: 'auto',
                            backgroundColor: 'var(--bg-light)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '0.375rem',
                            marginTop: '0.25rem',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                            zIndex: 1000
                          }}>
                            {availableCollections
                              .filter((collectionOption) => 
                                (collectionOption === col || !collections.includes(collectionOption)) &&
                                collectionOption.toLowerCase().includes(col.toLowerCase())
                              )
                              .map((collectionOption) => (
                                <div
                                  key={collectionOption}
                                  onClick={() => {
                                    updateCollection(index, collectionOption);
                                    setMultiCollectionDropdowns(prev => ({ ...prev, [index]: false }));
                                  }}
                                  style={{
                                    padding: '0.5rem 0.75rem',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    color: 'var(--text-dark)',
                                    backgroundColor: col === collectionOption ? 'var(--primary-color)' : 'transparent',
                                    transition: 'background-color 0.2s'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (col !== collectionOption) {
                                      e.currentTarget.style.backgroundColor = 'var(--bg-darker)';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (col !== collectionOption) {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                    }
                                  }}
                                >
                                  {collectionOption}
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                      {collections.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeCollection(index)}
                          style={{ 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0.5rem',
                            backgroundColor: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.375rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#b91c1c';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#dc2626';
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    size="sm"
                    onClick={addCollection}
                    style={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      backgroundColor: 'var(--primary-color)',
                      color: 'white',
                      border: 'none',
                      width: 'fit-content'
                    }}
                  >
                    <Plus size={14} style={{ marginRight: '0.25rem' }} /> Add Collection
                  </Button>
                </div>
              )}
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <label className="form-label" style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-dark)' }}>
                  Filters
                </label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => addFilterGroup(false)}
                    style={{ 
                      fontSize: '0.875rem', 
                      fontWeight: 500,
                      backgroundColor: 'var(--primary-color)',
                      color: 'white',
                      border: 'none'
                    }}
                  >
                    <Plus size={14} style={{ marginRight: '0.25rem' }} /> AND
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => addFilterGroup(true)}
                    style={{ 
                      fontSize: '0.875rem', 
                      fontWeight: 500,
                      backgroundColor: 'var(--primary-color)',
                      color: 'white',
                      border: 'none'
                    }}
                  >
                    <Plus size={14} style={{ marginRight: '0.25rem' }} /> OR
                  </Button>
                </div>
              </div>

              {filterGroups.length === 0 && (
                <div style={{ 
                  padding: '1rem', 
                  backgroundColor: 'var(--bg-light)', 
                  border: '1px dashed var(--border-color)', 
                  borderRadius: '0.375rem',
                  textAlign: 'center'
                }}>
                  <p style={{ color: 'var(--text-medium)', fontSize: '0.875rem', margin: 0 }}>
                    No filters added. Click <strong style={{ color: 'var(--primary-color)' }}>AND</strong> or <strong style={{ color: 'var(--primary-color)' }}>OR</strong> to start.
                  </p>
                </div>
              )}

              {filterGroups.map((group) => (
                <div
                  key={group.id}
                  style={{
                    border: '1px solid var(--border-color)',
                    borderRadius: '0.5rem',
                    padding: '1rem',
                    marginBottom: '0.75rem',
                    backgroundColor: 'var(--bg-light)',
                    boxShadow: 'var(--shadow-sm)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <span style={{ 
                      color: group.isOrGroup ? 'var(--secondary-color)' : 'var(--primary-color)', 
                      fontSize: '0.75rem', 
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      {group.isOrGroup ? 'OR Group' : 'AND Filter'}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFilterGroup(group.id)}
                      style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0.25rem 0.5rem',
                        backgroundColor: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.375rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#b91c1c';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#dc2626';
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>

                  {group.filters.map((filter, filterIndex) => (
                    <div
                      key={filterIndex}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1.5fr 110px 1.5fr auto',
                        gap: '0.5rem',
                        marginBottom: '0.5rem',
                        alignItems: 'center',
                      }}
                    >
                      <input
                        type="text"
                        className="input-field"
                        placeholder="Field name (e.g., entity)"
                        value={filter.field}
                        onChange={(e) =>
                          updateFilter(group.id, filterIndex, 'field', e.target.value)
                        }
                      />
                      <select
                        className="input-field"
                        value={filter.operator}
                        onChange={(e) =>
                          updateFilter(group.id, filterIndex, 'operator', e.target.value)
                        }
                        style={{
                          paddingRight: '2rem'
                        }}
                      >
                        {OPERATORS.map((op) => (
                          <option key={op} value={op}>
                            {op}
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        className="input-field"
                        placeholder={
                          filter.operator === 'in'
                            ? 'Comma-separated (e.g., val1, val2)'
                            : filter.operator === 'array-contains'
                            ? 'Array element value'
                            : 'Value'
                        }
                        value={String(filter.value || '')}
                        onChange={(e) =>
                          updateFilter(group.id, filterIndex, 'value', e.target.value)
                        }
                      />
                      {group.filters.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeFilterFromGroup(group.id, filterIndex)}
                          style={{ 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0.25rem 0.5rem',
                            minWidth: '2rem',
                            backgroundColor: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.375rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#b91c1c';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#dc2626';
                          }}
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  ))}

                  {group.isOrGroup && (
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => addFilterToGroup(group.id)}
                      style={{ 
                        width: '100%', 
                        marginTop: '0.5rem', 
                        fontSize: '0.875rem', 
                        fontWeight: 500,
                        backgroundColor: 'var(--primary-color)',
                        color: 'white',
                        border: 'none'
                      }}
                    >
                      <Plus size={14} style={{ marginRight: '0.25rem' }} /> Add Filter
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <label className="form-label" style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-dark)' }}>
                  Order By
                </label>
                <Button 
                  type="button" 
                  size="sm" 
                  onClick={addOrderBy} 
                  style={{ 
                    fontSize: '0.875rem', 
                    fontWeight: 500,
                    backgroundColor: 'var(--primary-color)',
                    color: 'white',
                    border: 'none'
                  }}
                >
                  <Plus size={14} style={{ marginRight: '0.25rem' }} /> Add
                </Button>
              </div>
              {orderBy.map((order, index) => (
                <div
                  key={index}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr auto',
                    gap: '0.5rem',
                    marginBottom: '0.5rem',
                    alignItems: 'center',
                  }}
                >
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Field name"
                    value={order.field}
                    onChange={(e) => updateOrderBy(index, 'field', e.target.value)}
                    style={{ fontSize: '0.875rem' }}
                  />
                  <select
                    className="input-field"
                    value={order.direction}
                    onChange={(e) => updateOrderBy(index, 'direction', e.target.value)}
                    style={{ 
                      fontSize: '0.875rem',
                      width: 'auto',
                      minWidth: 'fit-content',
                      paddingRight: '2rem'
                    }}
                  >
                    <option value="asc">Ascending</option>
                    <option value="desc">Descending</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => removeOrderBy(index)}
                    style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0.25rem 0.5rem',
                      minWidth: '2rem',
                      backgroundColor: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.375rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#b91c1c';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#dc2626';
                    }}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <label className="form-label" style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-dark)' }}>
                  Select Fields
                </label>
                <Button 
                  type="button" 
                  size="sm" 
                  onClick={addSelectedField} 
                  style={{ 
                    fontSize: '0.875rem', 
                    fontWeight: 500,
                    backgroundColor: 'var(--primary-color)',
                    color: 'white',
                    border: 'none'
                  }}
                >
                  <Plus size={14} style={{ marginRight: '0.25rem' }} /> Add
                </Button>
              </div>
              {selectedFields.map((field, index) => (
                <div
                  key={index}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: '0.5rem',
                    marginBottom: '0.5rem',
                    alignItems: 'center',
                  }}
                >
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Field name (e.g., data.tripId)"
                    value={field}
                    onChange={(e) => updateSelectedField(index, e.target.value)}
                    style={{ fontSize: '0.875rem' }}
                  />
                  <button
                    type="button"
                    onClick={() => removeSelectedField(index)}
                    style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0.25rem 0.5rem',
                      minWidth: '2rem',
                      backgroundColor: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.375rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#b91c1c';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#dc2626';
                    }}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--border-color)' }}>
              <Button
                type="button"
                onClick={handleExecute}
                disabled={queryIsLoading}
                style={{ 
                  flex: 1, 
                  fontSize: '0.875rem', 
                  fontWeight: 500,
                  backgroundColor: 'var(--primary-color)',
                  color: 'white',
                  border: 'none'
                }}
              >
                <Play size={16} style={{ marginRight: '0.5rem' }} /> {queryIsLoading ? 'Executing...' : 'Execute Query'}
              </Button>
              <Button
                type="button"
                onClick={() => setShowJson(!showJson)}
                style={{ 
                  fontSize: '0.875rem',
                  backgroundColor: 'var(--primary-color)',
                  color: 'white',
                  border: 'none'
                }}
              >
                <Code size={16} />
              </Button>
            </div>
          </CardContent>
        </Card>

        <QueryResults
          queryData={queryData}
          isLoading={queryIsLoading}
          error={queryError}
          multiCollectionMode={multiCollectionMode}
          collectionFilter={collectionFilter}
          collections={collections}
          pageSize={pageSize}
          currentPage={currentPage}
          showJson={showJson}
          queryJson={getQueryJson()}
          onRefresh={() => refetchQuery()}
          onCollectionFilterChange={handleCollectionFilterChange}
          onPageSizeChange={handlePageSizeChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onDocumentClick={handleDocumentClick}
          onCopyJson={() => navigator.clipboard.writeText(getQueryJson())}
        />
      </div>

      {/* Raw Document Edit Modal (super_admin only) */}
      {editTarget && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setEditTarget(null)}
        >
          <div
            style={{
              backgroundColor: 'var(--bg-light)',
              borderRadius: '0.5rem',
              padding: '1.5rem',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0, color: 'var(--text-dark)' }}>
                Edit Document — {editTarget.collection}/{editTarget.id}
              </h2>
              <Button
                type="button"
                onClick={() => setEditTarget(null)}
                style={{
                  backgroundColor: 'transparent',
                  color: 'var(--text-medium)',
                  border: 'none',
                  padding: '0.25rem',
                }}
                title="Close"
              >
                <X size={18} />
              </Button>
            </div>
            {editDocumentLoading && (
              <p style={{ color: 'var(--text-medium)' }}>Loading document…</p>
            )}
            {editDocumentError && (
              <p style={{ color: 'var(--danger-color, #ef4444)' }}>
                Failed to load document: {(editDocumentError as Error).message}
              </p>
            )}
            {editDocument && (
              <DocumentEditor
                document={editDocument}
                onSave={handleRawDocumentSave}
              />
            )}
          </div>
        </div>
      )}

      {/* Save Query Modal */}
      {showSaveModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowSaveModal(false)}
        >
          <div
            style={{
              backgroundColor: 'var(--bg-light)',
              borderRadius: '0.5rem',
              padding: '2rem',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '80vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1rem', color: 'var(--text-dark)' }}>
              Save Query
            </h2>
            <div style={{ marginBottom: '1rem' }}>
              <label className="form-label" style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-dark)', display: 'block', marginBottom: '0.5rem' }}>
                Query Name
              </label>
              <input
                type="text"
                className="input-field"
                value={saveQueryName}
                onChange={(e) => setSaveQueryName(e.target.value)}
                placeholder="My Query"
                style={{ fontSize: '0.875rem', width: '100%' }}
              />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label className="form-label" style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-dark)', display: 'block', marginBottom: '0.5rem' }}>
                Description (optional)
              </label>
              <textarea
                className="input-field"
                value={saveQueryDescription}
                onChange={(e) => setSaveQueryDescription(e.target.value)}
                placeholder="Describe what this query does..."
                style={{ fontSize: '0.875rem', width: '100%', minHeight: '80px', resize: 'vertical' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <Button
                  type="button"
                onClick={() => setShowSaveModal(false)}
                  style={{
                    fontSize: '0.875rem',
                    backgroundColor: 'var(--bg-darker)',
                    color: 'var(--text-dark)',
                    border: '1px solid var(--border-color)'
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleSaveQuery}
                  disabled={!saveQueryName.trim() || saveQueryMutation.isPending}
                  style={{
                    fontSize: '0.875rem',
                    backgroundColor: 'var(--primary-color)',
                    color: 'white',
                  border: 'none'
                  }}
                >
                {saveQueryMutation.isPending ? 'Saving...' : 'Save'}
                </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default QueryBuilder;
